import React, { useState } from "react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";

export default function Settings() {
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "", phone: user?.phone || "", city: user?.city || "",
    state: user?.state || "", bio: user?.bio || "",
  });
  const [saving, setSaving] = useState(false);

  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const setPwField = (k) => (e) => setPw({ ...pw, [k]: e.target.value });

  const submitPassword = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (pw.new_password !== pw.confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setPwSaving(true);
    try {
      await api.put("/auth/password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Password updated.");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setPwSaving(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile(form);
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-page" className="max-w-2xl">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-patriot font-bold">Account</p>
        <h1 className="font-display text-3xl text-navy mt-1">Settings</h1>
      </div>

      <form onSubmit={submit} className="bg-white border border-border rounded-xl p-8 space-y-5" data-testid="settings-form">
        <div>
          <Label className="text-navy">Email</Label>
          <Input value={user?.email} disabled className="mt-1.5 bg-muted" />
        </div>
        <div>
          <Label className="text-navy">Full name</Label>
          <Input value={form.name} onChange={set("name")} data-testid="settings-name" className="mt-1.5" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-navy">Phone</Label><Input value={form.phone} onChange={set("phone")} className="mt-1.5" /></div>
          <div><Label className="text-navy">City</Label><Input value={form.city} onChange={set("city")} className="mt-1.5" /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-navy">State</Label><Input value={form.state} onChange={set("state")} className="mt-1.5" /></div>
          <div><Label className="text-navy">Referral code</Label><Input value={user?.referral_code} disabled className="mt-1.5 bg-muted font-mono" /></div>
        </div>
        <div>
          <Label className="text-navy">Bio</Label>
          <Textarea value={form.bio} onChange={set("bio")} data-testid="settings-bio" className="mt-1.5" rows={3} placeholder="Tell physicians and recruits about yourself..." />
        </div>
        <Button type="submit" disabled={saving} data-testid="settings-save" className="rounded-full bg-navy hover:bg-navy-dark text-white">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
        </Button>
      </form>

      <form onSubmit={submitPassword} className="bg-white border border-border rounded-xl p-8 space-y-5 mt-8" data-testid="password-form">
        <div className="flex items-center gap-2">
          <Lock className="h-5 w-5 text-navy" />
          <h2 className="font-display text-xl text-navy">Change password</h2>
        </div>
        <p className="text-sm text-muted-foreground -mt-2">Update the password you use to sign in.</p>
        <div>
          <Label className="text-navy">Current password</Label>
          <Input type="password" value={pw.current_password} onChange={setPwField("current_password")} required data-testid="current-password" className="mt-1.5" placeholder="••••••••" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-navy">New password</Label>
            <Input type="password" value={pw.new_password} onChange={setPwField("new_password")} required data-testid="new-password" className="mt-1.5" placeholder="At least 6 characters" />
          </div>
          <div>
            <Label className="text-navy">Confirm new password</Label>
            <Input type="password" value={pw.confirm} onChange={setPwField("confirm")} required data-testid="confirm-password" className="mt-1.5" placeholder="Re-enter new password" />
          </div>
        </div>
        <Button type="submit" disabled={pwSaving} data-testid="password-save" className="rounded-full bg-navy hover:bg-navy-dark text-white">
          {pwSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
        </Button>
      </form>
    </div>
  );
}
